<template>
  <SoDialog
    ref="enrolment-details-modal"
    v-model="isOpen"
    class="enrolment-modal"
    :loading="accountMeterpointsQuery?.isLoading"
  >
    <div class="d-flex align-center justify-space-between mb-4">
      <h5>Overview</h5>
      <SoStatus :status="enrolment.mappedStatus?.status" />
    </div>
    <SoSimpleTable
      ref="overview-table"
      :headers="Object.keys(overviewDetails)"
      :items="[Object.values(overviewDetails)]"
      is-pivoted
      equal-width-columns
    />
    <h5 class="mb-4 mt-3">Meterpoints</h5>

    <div
      v-if="accountMeterpointsQuery?.isFetching"
      data-testid="enrolment-modal-meterpoint-fetch-loading"
    >
      <SoSkeleton height="176px" />
    </div>
    <SoAlert
      v-else-if="accountMeterpointsQuery?.error"
      type="error"
      data-testid="enrolment-modal-meterpoint-fetch-error"
    >
      Could not fetch meterpoint information
    </SoAlert>
    <SoAlert
      v-else-if="!enrolmentMeterpointsList?.length"
      type="info"
      data-testid="enrolment-modal-no-meterpoints"
    >
      Therer are no meterpoints linked to this enrolment
    </SoAlert>
    <SoWidget v-else plain no-border class="mt-3">
      <SoRow>
        <SoCol
          v-for="(
            { meterpoint, productCode, estimatedAnnualUsage }, index
          ) in enrolmentMeterpointsList"
          :key="index"
          :default-width="6"
        >
          <SoWidget>
            <template #title>
              <MeterpointHeader :meterpoint="meterpoint" class="mb-2" />
            </template>
            <template #text>
              <div class="enrolment-modal__text-layout-grid">
                <div class="text-medium-emphasis">Tariff code</div>
                <div ref="meterpoint-tariff">{{ productCode }}</div>
                <div class="text-medium-emphasis">Supply start date</div>
                <div ref="meterpoint-supply-start-date">
                  {{
                    meterpoint.supplyStartDate
                      ? formatDate(meterpoint.supplyStartDate)
                      : "-"
                  }}
                </div>
                <div class="text-medium-emphasis">Supply status</div>
                <div ref="meterpoint-status">
                  <SoStatus
                    v-if="meterpoint.status"
                    :status="meterpoint.status"
                  /><template v-else>-</template>
                </div>
                <div class="text-medium-emphasis">Estimated usage*</div>
                <div>{{ estimatedAnnualUsage || "-" }}</div>
              </div>
            </template>
          </SoWidget>
        </SoCol>
      </SoRow>
      <div class="text-medium-emphasis mt-3">
        *Annual usage entered by user during registration
      </div>
    </SoWidget>
    <div class="d-flex align-center justify-space-between mb-4 mt-6">
      <h5>Personal details</h5>
      <SoLink
        v-if="enrolment.accountNumber"
        text="Open account"
        :href="`/customer/${enrolment.accountNumber}`"
        icon="external-link sm"
      />
    </div>
    <SoSimpleTable
      ref="personal-details-table"
      :headers="Object.keys(personalDetails)"
      :items="[Object.values(personalDetails)]"
      is-pivoted
      equal-width-columns
    />
    <h5 class="mb-4 mt-5">Payment details</h5>
    <SoSimpleTable
      ref="payment-details-table"
      :headers="Object.keys(paymentDetails)"
      :items="[Object.values(paymentDetails)]"
      is-pivoted
      equal-width-columns
    />
    <div
      v-if="isFFEnabled"
      class="d-flex justify-end mb-4 mt-6 align-center gap-4"
    >
      <p :class="`text-${retryEnrolmentStatus.type}`">
        {{ retryEnrolmentStatus.text }}
      </p>
      <SoButton
        title="Retry"
        :loading="enrolmentMutator.isPending"
        @click="retry"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { PropType, defineComponent } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import SoLink from "@/components/SoLink.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import MeterpointHeader from "@/views/customer/components/MeterpointHeader.vue"
  import { formatDateTime, formatDate } from "@/helpers/dateHelper"
  import { IEnrolment } from "@/models/operations/enrolment/enrolment.types"
  import { CoreMeterpointService } from "@/services/admin/CoreMeterpointService"
  import { yesNo } from "@/helpers/stringHelpers"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { EnrolmentMutator } from "@/mutators/enrolment/EnrolmentMutator"
  import { Status } from "@/types/Status"
  import { EnrolmentRetryResponse } from "@/api/backofficeRest/generated/model"
  import { isBackofficeApiError } from "@/api/backofficeRest/backofficeApiError"
  import { FeaturesService } from "@/services/admin/FeaturesService"

  export default defineComponent({
    components: {
      SoAlert,
      SoCol,
      SoDialog,
      SoRow,
      SoLink,
      SoSimpleTable,
      SoSkeleton,
      SoStatus,
      SoWidget,
      MeterpointHeader,
      SoButton,
    },
    props: {
      modelValue: {
        type: Boolean,
        required: true,
      },
      enrolment: {
        type: Object as PropType<IEnrolment>,
        default: null,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        formatDate,
        enrolmentMutator: EnrolmentMutator.retryEnrolment(),
      }
    },
    computed: {
      isFFEnabled() {
        return FeaturesService.isFeatureActive("MANUAL_ENROLMENT_RETRY")
      },
      accountId() {
        return this.enrolment.coreAccountId
      },
      accountMeterpointsQuery() {
        return this.accountId
          ? CoreMeterpointService.getForAccount(this.accountId)
          : null
      },
      enrolmentMeterpointsList() {
        const enrolmentAgreements = this.enrolment.agreements
        const accountMeterpoints = this.accountMeterpointsQuery?.data
        if (accountMeterpoints?.length) {
          return accountMeterpoints?.map((meterpoint) => {
            const meterpointAgreement = enrolmentAgreements.find(
              (agreement) =>
                agreement.meterpointIdentifier === meterpoint.identifier,
            )
            return {
              ...meterpointAgreement,
              meterpoint,
            }
          })
        }

        // If enrolment has no account, we can only show limited meterpoint info
        // As these are not yet attached to an account, they have no SSD or status
        return enrolmentAgreements.map((meterpointAgreement) => ({
          ...meterpointAgreement,
          meterpoint: {
            meterpointType: meterpointAgreement.isElectricity ? "MPAN" : "MPRN",
            identifier: meterpointAgreement.meterpointIdentifier,
            supplyStartDate: null,
            status: null,
          },
        }))
      },
      overviewDetails() {
        return {
          Email: this.enrolment.email,
          Broker: this.enrolment.broker,
          Reference: this.enrolment.thirdPartyReference || "-",
          Error: this.enrolment.enrolmentFailureMessage || "-",
          "Submitted at": formatDateTime(this.enrolment.createdAt),
        }
      },
      personalDetails() {
        return {
          Name: this.enrolment.customerName || "-",
          "Phone number": this.enrolment.contact.phoneNumber || "-",
          "Date of birth": this.enrolment.contact.dateOfBirth || "-",
          "Supply address":
            this.enrolment.supplyAddress?.formattedAddress || "-",
          "Contact address":
            this.enrolment.contactAddress?.formattedAddress || "-",
          "Accepted marketing": yesNo(this.enrolment.marketingOptIn),
          "Priority Services": yesNo(this.enrolment.requestedPsr),
          "Account Number": this.enrolment.accountNumber || "-",
          "Customer Number": this.enrolment.customerNumber || "-",
        }
      },
      paymentDetails() {
        const paymentDetails = this.enrolment.paymentDetails
        return {
          "Account Name": paymentDetails.accountName,
          "Account Number": paymentDetails.accountNumber,
          "Sort code": paymentDetails.sortCode,
          "Payment amount": `£${paymentDetails.paymentAmount}`,
          "Payment day of the month": `${paymentDetails.paymentDayOfMonth}th`,
          "Seasonal payments": yesNo(paymentDetails.seasonalPayment),
        }
      },
      isOpen: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      retryEnrolmentStatus() {
        const error = this.enrolmentMutator.error

        if (isBackofficeApiError(error)) {
          const text =
            error.status === 404 || error.errorCode === "404"
              ? "Enrolment is not in a retry status"
              : error.userMessage
          return new Status("critical", text)
        }

        const retryData = this.enrolmentMutator.data as
          EnrolmentRetryResponse | undefined

        if (!retryData) return new Status("normal", "")

        return new Status(
          "normal",
          `Enrolment retried successfully. New status: ${retryData.status}`,
        )
      },
    },
    watch: {
      "enrolment.id": function () {
        this.enrolmentMutator.reset()
      },
    },
    methods: {
      async retry() {
        // The outcome is rendered inline from the mutator state, so the
        // rejection is handled here and must not reach the global snackbar.
        await this.enrolmentMutator
          .mutateAsync(this.enrolment.id)
          .catch(() => undefined)
      },
    },
  })
</script>

<style scoped>
  .enrolment-modal__text-layout-grid {
    display: grid;
    grid-template-columns: 2fr 2fr;
    gap: 16px;
  }
</style>
